import { h, Fragment } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useLocalizationContext } from '@js/contexts/LocalizationsContext';
import { get as _get } from 'lodash-es';

const translate = (localization, path, replacements = null) => {
  let translation = _get(localization, path);

  if (!translation) {
    return `missing translation path: [context: ${localization}, path: ${path}]`;
  }

  // support plurals
  if (
    Object.prototype.hasOwnProperty.call(translation, 'one') &&
    Object.prototype.hasOwnProperty.call(translation, 'other')
  ) {
    if (replacements !== null) {
      const count = replacements.count || 0;
      translation = translation[count === 1 ? 'one' : 'other'];
    } else {
      translation = translation.one;
    }
  }

  // find and replace all occurrences starting with `$`
  if (replacements !== null) {
    translation = Object.keys(replacements).reduce((acc, key) => {
      return acc.replace(`$${key}`, replacements[key]);
    }, translation);
  }

  // replace character entities: https://oinam.github.io/entities/
  return translation.replace(/&#(\d+);/g, (_match, group) => String.fromCharCode(group));
};

const translateWithComponent = (localization, path, replacements) => {
  const translation = _get(localization, path);

  if (!translation) {
    return `missing translation path: [path: '${path}]'`;
  }

  const patterns = Object.keys(replacements)
    .reduce((acc, key) => `${acc}|\\$${key}`, '')
    .substring(1);
  const re = new RegExp(`(${patterns})`, 'u');

  return translation.split(re).reduce((acc, el) => {
    const key = el.substring(1);
    const replaceWith = replacements[key];

    if (replaceWith) {
      return [...acc, h(Fragment, null, replaceWith)];
    }

    return [...acc, el];
  }, []);
};

/**
 * Setup a state and use the global `__slrsTranslations` object as a base.
 *
 * @returns fn that returns an object to be use with `useLocalization`
 */
export const useLocalizationState = () => {
  const [state, setState] = useState(window.__slrsTranslations || null);

  // update our global object
  useEffect(() => {
    window.__slrsTranslations = state;
  }, [state]);

  return () => ({
    localization: state,
    setLocalization: setState,
  });
};

/**
 * Returns a fn named `t` to start using localized content.
 * (!! Note: can only be used in components wrapped within a LocalizationProvider)
 *
 * @example
 * // returns "Hello world!"
 * `t('general.hello)`
 *
 * // returns "Hello, Lorenzo!"
 * `t('general.helloUser', { name: 'Lorenzo' })`
 *
 * @param {string} selector id of html <script> element containing localizations in a json format.
 */
export const useLocalization = (selector, context = useLocalizationContext) => {
  const { localization, setLocalization } = context();

  useEffect(() => {
    if (localization === null || !localization[selector]) {
      try {
        const localizationContent = JSON.parse(document.getElementById(selector).textContent);

        setLocalization({
          ...localization,
          [selector]: localizationContent,
        });
      } catch (e) {
        console.warn(`[Localization] failed parsing json for #${selector}`);
      }
    }
  }, [localization, setLocalization, selector]);

  const t = useCallback(
    (path, replacements = null) => {
      if (localization === null) {
        return null;
      }

      if (!localization[selector]) {
        return `missing translation context: ${selector}`;
      }

      return translate(localization[selector], path, replacements);
    },
    [localization, selector],
  );

  const tWithComponent = useCallback(
    (path, replacements) => {
      if (localization === null) {
        return null;
      }

      if (!localization[selector]) {
        return `missing translation context: ${selector}`;
      }

      return h(Fragment, null, translateWithComponent(localization[selector], path, replacements));
    },
    [localization, selector],
  );

  return {
    t,
    tWithComponent,
  };
};

export default {
  useLocalization,
  useLocalizationState,
};
